import type { WorldcupResearchQuery } from '~/features/worldcup-research/types'
import { z } from 'zod'

const competitionCodes = [
  'world_cup',
  'euro',
  'copa_america',
  'nations_league',
  'world_cup_qualifier',
  'friendlies',
] as const

const providers = ['statsbomb', 'api_football'] as const
const fieldConfidenceValues = [
  'trusted',
  'definition_sensitive',
  'provider_specific',
  'single_source',
  'unverified',
  'rejected',
] as const
const researchViews = ['major_tournament', 'competitive_context', 'exploratory_all'] as const
const sortFields = ['season', 'team', 'opponent', 'goals', 'shotsOnTarget', 'corners', 'shotsTotal', 'expectedGoals'] as const
const sortDirections = ['asc', 'desc'] as const
export const worldcupResearchDefaultFromYear = 2010

export const worldcupResearchDefaultSearch = {
  competitionCode: 'world_cup',
  competitionCodes: ['world_cup', 'euro', 'copa_america'],
  fromYear: worldcupResearchDefaultFromYear,
  researchView: 'major_tournament',
  sortDirection: 'asc',
  limit: 25,
  offset: 0,
} satisfies Pick<
  WorldcupResearchQuery,
  'competitionCode' | 'competitionCodes' | 'fromYear' | 'researchView' | 'sortDirection' | 'limit' | 'offset'
>

const optionalTrimmedString = z.preprocess(
  value => typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined,
  z.string().optional(),
)

const optionalInt = z.preprocess(
  value => value === '' || value === null ? undefined : value,
  z.coerce.number().int().optional(),
)

const optionalNumber = z.preprocess(
  value => value === '' || value === null ? undefined : value,
  z.coerce.number().optional(),
)

function splitCompetitionCodesParam(value: unknown): string[] | undefined {
  if (value === undefined || value === null || value === '')
    return undefined

  const values = Array.isArray(value) ? value : [value]
  return values
    .flatMap((item) => {
      const text = String(item).trim()
      if (text.startsWith('[')) {
        try {
          const parsed: unknown = JSON.parse(text)
          if (Array.isArray(parsed))
            return parsed.map(entry => String(entry))
        }
        catch {
          return [text]
        }
      }
      return text.split(',')
    })
    .map(item => item.trim())
    .filter(Boolean)
}

export function researchViewForCompetition(
  competitionCode: WorldcupResearchQuery['competitionCode'],
): NonNullable<WorldcupResearchQuery['researchView']> {
  if (competitionCode === 'friendlies')
    return 'exploratory_all'
  if (competitionCode === 'nations_league' || competitionCode === 'world_cup_qualifier')
    return 'competitive_context'
  return 'major_tournament'
}

export function researchViewForCompetitions(
  competitionCodes: WorldcupResearchQuery['competitionCodes'],
): NonNullable<WorldcupResearchQuery['researchView']> {
  if (competitionCodes?.includes('friendlies'))
    return 'exploratory_all'
  if (competitionCodes?.some(code => code === 'nations_league' || code === 'world_cup_qualifier'))
    return 'competitive_context'
  return 'major_tournament'
}

const competitionCodesParam = z.preprocess(
  splitCompetitionCodesParam,
  z.array(z.enum(competitionCodes)).min(1).optional(),
)

export const worldcupResearchSearchSchema = z.object({
  competitionCode: z.enum(competitionCodes).optional().catch('world_cup'),
  competitionCodes: competitionCodesParam.catch(undefined),
  competitionName: optionalTrimmedString.catch(undefined),
  season: optionalInt.pipe(z.number().min(1990).max(2030).optional()).catch(undefined),
  fromYear: optionalInt.pipe(z.number().min(1900).max(2030).optional()).catch(undefined),
  toYear: optionalInt.pipe(z.number().min(1900).max(2030).optional()).catch(undefined),
  team: optionalTrimmedString.catch(undefined),
  opponent: optionalTrimmedString.catch(undefined),
  provider: z.enum(providers).optional().catch(undefined),
  confidence: z.enum(fieldConfidenceValues).optional().catch(undefined),
  researchView: z.enum(researchViews).optional().catch(undefined),
  minDataQualityScore: optionalNumber.pipe(z.number().min(0).max(1).optional()).catch(undefined),
  sort: z.enum(sortFields).optional().catch(undefined),
  sortDirection: z.enum(sortDirections).optional().catch(undefined),
  limit: optionalInt.pipe(z.number().min(1).max(500).optional()).catch(undefined),
  offset: optionalInt.pipe(z.number().min(0).optional()).catch(undefined),
}).transform((search): WorldcupResearchQuery => {
  const competitionCodes = search.competitionCodes ?? (search.competitionCode ? [search.competitionCode] : worldcupResearchDefaultSearch.competitionCodes)
  const competitionCode = competitionCodes[0] ?? 'world_cup'
  const fromYear = search.fromYear ?? worldcupResearchDefaultFromYear
  const toYear = search.toYear && search.toYear >= fromYear ? search.toYear : undefined
  return {
    competitionCode,
    competitionCodes,
    competitionName: search.competitionName,
    season: search.season as WorldcupResearchQuery['season'] | undefined,
    fromYear,
    toYear,
    team: search.team,
    opponent: search.opponent,
    provider: search.provider,
    confidence: search.confidence,
    researchView: search.researchView ?? researchViewForCompetitions(competitionCodes),
    minDataQualityScore: search.minDataQualityScore,
    sort: search.sort,
    sortDirection: search.sortDirection ?? 'asc',
    limit: search.limit ?? 25,
    offset: search.offset ?? 0,
  }
})

export type WorldcupResearchSearch = z.infer<typeof worldcupResearchSearchSchema>

export interface GuidedWorldcupDraft {
  team: string
  opponent: string
  competitionCodes: string[]
  competitionName: string
  season: string
  fromYear: string
  toYear: string
}

export function draftFromGuidedFilters(filters: WorldcupResearchQuery): GuidedWorldcupDraft {
  return {
    team: filters.team ?? '',
    opponent: filters.opponent ?? '',
    competitionCodes: filters.competitionCodes?.length ? filters.competitionCodes : [filters.competitionCode ?? 'world_cup'],
    competitionName: filters.competitionName ?? '',
    season: filters.season ? String(filters.season) : '',
    fromYear: filters.fromYear ? String(filters.fromYear) : '',
    toYear: filters.toYear ? String(filters.toYear) : '',
  }
}

export function cleanWorldcupSearch(filters: WorldcupResearchQuery): Record<string, string | number> {
  const next: Record<string, string | number | undefined> = {
    competitionCodes: filters.competitionCodes?.length ? filters.competitionCodes.join(',') : filters.competitionCode,
    competitionName: filters.competitionName,
    season: filters.season,
    fromYear: filters.fromYear === worldcupResearchDefaultFromYear ? undefined : filters.fromYear,
    toYear: filters.toYear,
    team: filters.team,
    opponent: filters.opponent,
    provider: filters.provider,
    confidence: filters.confidence,
    researchView: filters.researchView,
    minDataQualityScore: filters.minDataQualityScore,
    sort: filters.sort,
    sortDirection: filters.sortDirection === 'asc' ? undefined : filters.sortDirection,
    limit: filters.limit === 25 ? undefined : filters.limit,
    offset: filters.offset === 0 ? undefined : filters.offset,
  }
  return Object.fromEntries(Object.entries(next).filter(([, value]) => value !== undefined && value !== '')) as Record<string, string | number>
}
